import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Mail, MapPin, Clock } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

const TABS = [
  { id: "contact", label: "Contact" },
  { id: "mentions-legales", label: "Mentions légales" },
  { id: "cgv", label: "CGV" },
  { id: "confidentialite", label: "Confidentialité" },
  { id: "remboursement", label: "Remboursement" },
];

const Article = ({ title, children }) => (
  <div className="legal-article">
    <h3>{title}</h3>
    {children}
  </div>
);

function ContactSection() {
  return (
    <div data-testid="section-contact">
      <p className="legal-intro">
        Une question sur les guides, ta commande ou la méthode ? Voici tous les
        moyens de nous joindre.
      </p>
      <div className="contact-cards">
        <a
          href="mailto:Bathilysamir22@gmail.com"
          className="contact-card"
          data-testid="contact-email-card"
        >
          <Mail size={22} />
          <b>Email</b>
          <span>Bathilysamir22@gmail.com</span>
        </a>
        <div className="contact-card" data-testid="contact-address-card">
          <MapPin size={22} />
          <b>Courrier</b>
          <span>15 rue des Halles, 75001 Paris</span>
        </div>
        <div className="contact-card" data-testid="contact-delay-card">
          <Clock size={22} />
          <b>Délai de réponse</b>
          <span>Sous 48 h ouvrées</span>
        </div>
      </div>
      <Article title="Avant d'écrire">
        <p>
          Pour toute question sur une commande, indique l'adresse email utilisée
          lors de l'achat et, si possible, le numéro de commande : cela nous
          permet de te répondre plus vite. Pour un problème de téléchargement,
          précise le guide concerné (mémorisation, révision ou Pack Gold).
        </p>
      </Article>
    </div>
  );
}

function MentionsSection() {
  return (
    <div data-testid="section-mentions-legales">
      <Article title="Éditeur du site">
        <p>
          SamNara, Société par actions simplifiée (SAS) — 15 rue
          des Halles, 75001 Paris. Représentant légal : Aboubacar N'Diaye.
          Contact : Bathilysamir22@gmail.com.
        </p>
      </Article>
      <Article title="Immatriculation">
        <p>
          SIREN : 911 757 003 — SIRET : 911 757 003 00015 — Immatriculée le 25
          mars 2022.
        </p>
      </Article>
      <Article title="Nature des produits">
        <p>
          Produits vendus sous forme de fichiers numériques (ebooks PDF),
          livrés par téléchargement immédiat après paiement. Ces guides sont des
          outils pédagogiques indépendants, non affiliés à une organisation
          religieuse officielle.
        </p>
      </Article>
      <Article title="Propriété intellectuelle">
        <p>
          L'ensemble des contenus du site et des guides (textes, visuels,
          structure) est protégé par le droit d'auteur. Toute reproduction ou
          diffusion sans autorisation écrite est interdite.
        </p>
      </Article>
    </div>
  );
}

function CgvSection() {
  return (
    <div data-testid="section-cgv">
      <Article title="1. Objet">
        <p>
          Les présentes conditions générales de vente régissent la vente des
          guides numériques « Guide de mémorisation du Coran — 6 mois », « Guide
          de révision du Coran — 30 jours » et du « Pack Gold » proposés par
          la SAS SamNara.
        </p>
      </Article>
      <Article title="2. Prix">
        <p>
          Les prix sont indiqués en euros, toutes taxes comprises. La SAS se
          réserve le droit de modifier ses prix à tout moment ; le prix
          applicable est celui en vigueur au moment de la commande.
        </p>
      </Article>
      <Article title="3. Commande et paiement">
        <p>
          La commande est validée après paiement intégral, effectué via un
          prestataire de paiement sécurisé. La SAS ne conserve aucune donnée
          bancaire.
        </p>
      </Article>
      <Article title="4. Livraison">
        <p>
          Les guides sont livrés sous forme de fichiers numériques (PDF) par
          téléchargement immédiat après confirmation du paiement, ainsi que par
          l'envoi d'un lien à l'adresse email indiquée lors de la commande.
        </p>
      </Article>
      <Article title="5. Droit de rétractation">
        <p>
          Conformément à l'article L221-28 du Code de la consommation, le droit
          de rétractation ne s'applique pas aux contenus numériques dont
          l'exécution a commencé avec l'accord préalable et exprès du client,
          qui renonce à ce droit en validant sa commande.
        </p>
      </Article>
      <Article title="6. Usage personnel">
        <p>
          Les guides sont concédés pour un usage strictement personnel. Toute
          revente, partage ou diffusion des fichiers est interdit.
        </p>
      </Article>
      <Article title="7. Litiges">
        <p>
          Les présentes CGV sont soumises au droit français. En cas de litige,
          contacte-nous d'abord à Bathilysamir22@gmail.com pour une résolution
          amiable. À défaut, les tribunaux français seront compétents.
        </p>
      </Article>
    </div>
  );
}

function ConfidentialiteSection() {
  return (
    <div data-testid="section-confidentialite">
      <Article title="Responsable de traitement">
        <p>
          SAS SamNara — 15 rue des Halles, 75001 Paris — contact
          vie privée : Bathilysamir22@gmail.com.
        </p>
      </Article>
      <Article title="Données collectées">
        <p>
          Lors d'une commande : nom, adresse email et informations de
          facturation. Les données bancaires sont traitées uniquement par le
          prestataire de paiement et ne transitent pas par nos serveurs.
        </p>
      </Article>
      <Article title="Finalités">
        <p>
          Les données sont utilisées pour exécuter la commande, livrer les
          guides, assurer le suivi client et répondre aux demandes. Elles ne
          sont ni vendues ni partagées à des tiers à des fins commerciales.
        </p>
      </Article>
      <Article title="Conservation">
        <p>
          Les données de commande sont conservées pendant la durée légale
          comptable, puis supprimées ou anonymisées.
        </p>
      </Article>
      <Article title="Tes droits (RGPD)">
        <p>
          Tu disposes d'un droit d'accès, de rectification, d'effacement, de
          portabilité et d'opposition. Pour l'exercer, écris à
          Bathilysamir22@gmail.com. Tu peux également saisir la CNIL en cas de
          désaccord.
        </p>
      </Article>
      <Article title="Cookies">
        <p>
          Le site n'utilise pas de cookies publicitaires. Seuls des cookies
          strictement nécessaires au fonctionnement peuvent être déposés.
        </p>
      </Article>
    </div>
  );
}

function RemboursementSection() {
  return (
    <div data-testid="section-remboursement">
      <Article title="Produits numériques">
        <p>
          Conformément à l'article L221-28 du Code de la consommation, les
          contenus numériques téléchargés ne peuvent faire l'objet d'un
          remboursement une fois le téléchargement commencé avec ton accord
          exprès.
        </p>
      </Article>
      <Article title="Cas donnant droit à remboursement">
        <p>
          Un remboursement ou un nouvel envoi est possible si : le fichier est
          corrompu ou illisible, le lien de téléchargement ne fonctionne pas, ou
          le guide reçu ne correspond pas à ta commande.
        </p>
      </Article>
      <Article title="Procédure">
        <p>
          Écris à Bathilysamir22@gmail.com dans les 14 jours suivant l'achat en
          précisant ton adresse email de commande et le problème rencontré.
          Nous répondons sous 48 h ouvrées.
        </p>
      </Article>
      <Article title="Délai de remboursement">
        <p>
          Une fois la demande acceptée, le remboursement est effectué sur le
          moyen de paiement d'origine sous 14 jours maximum.
        </p>
      </Article>
    </div>
  );
}

const SECTIONS = {
  contact: <ContactSection />,
  "mentions-legales": <MentionsSection />,
  cgv: <CgvSection />,
  confidentialite: <ConfidentialiteSection />,
  remboursement: <RemboursementSection />,
};

export default function ContactPage() {
  const location = useLocation();
  const [active, setActive] = useState("contact");

  useEffect(() => {
    const hash = location.hash.replace("#", "");
    if (SECTIONS[hash]) setActive(hash);
    window.__lenis?.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
  }, [location.hash]);

  const select = (id) => {
    setActive(id);
    window.history.replaceState(null, "", `#${id}`);
  };

  return (
    <div className="info-page" data-testid="contact-page">
      <header className="info-header">
        <Link to="/" className="info-back" data-testid="back-home-link">
          <ArrowLeft size={16} />
          Retour à l'accueil
        </Link>
        <Link to="/" className="logo logo-link" data-testid="brand-home-link" aria-label="SamNara — accueil"><img src="/logo-samnara.png" alt="SamNara" className="logo-img logo-img-lg" /></Link>
      </header>

      <div className="container">
        <motion.div
          className="info-hero"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: EASE }}
        >
          <div className="eyebrow">Nous écrire</div>
          <h1>Contact &amp; informations légales</h1>
        </motion.div>

        <div className="info-layout">
          <nav className="info-tabs" data-testid="info-tabs">
            {TABS.map((t) => (
              <button
                key={t.id}
                className={`info-tab ${active === t.id ? "active" : ""}`}
                onClick={() => select(t.id)}
                data-testid={`tab-${t.id}`}
              >
                {t.label}
              </button>
            ))}
          </nav>

          <div className="info-panel" data-testid="info-panel">
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -14 }}
                transition={{ duration: 0.45, ease: EASE }}
              >
                {SECTIONS[active]}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>

      <footer className="info-footer">
        <div className="copyright">
          © SamNara — Guides numériques, livraison instantanée après achat
        </div>
      </footer>
    </div>
  );
}
